// src/app/features/admin/store/admin.effects.ts

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, finalize, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { UserManagementApiService } from '../services/user-management-api.service';
import {
  AdminUsersActions, AdminBranchesActions, AdminUserDetailActions,
} from './admin.actions';
import {
  selectAdminUsers, selectAdminBranches, selectAdminUserDetailMap,
} from './admin.selectors';
import { isCacheValid } from './admin.state';

@Injectable()
export class AdminEffects {
  private readonly actions$ = inject(Actions);
  private readonly store    = inject(Store);
  private readonly api      = inject(UserManagementApiService);
  private readonly translate = inject(TranslateService);
  private readonly _inFlight = new Set<number>();

  private readError(err: unknown): string {
    const e = err as any;
    return e?.error?.message ?? e?.message ?? this.translate.instant('ERRORS.UNEXPECTED_DOT');
  }

  // ── Users (switchMap — auto-cancel on filter change) ─────────────

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUsersActions.load),
      withLatestFrom(this.store.select(selectAdminUsers)),
      switchMap(([{ filter, forceReload }, slice]) => {
        // Reducer already guards against cache-valid + same-filter — only skip on cache hit here
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getUsers(filter).pipe(
          map(data => AdminUsersActions.success({ data, filter })),
          catchError(err => of(AdminUsersActions.failure({ error: this.readError(err) }))),
        );
      }),
    ),
  );

  // ── Branches (static, exhaustMap-like with cache check) ──────────

  loadBranches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminBranchesActions.load),
      withLatestFrom(this.store.select(selectAdminBranches)),
      switchMap(([{ forceReload }, slice]) => {
        if (!forceReload && isCacheValid(slice.cache)) return EMPTY;
        return this.api.getBranches().pipe(
          map(data => AdminBranchesActions.success({ data })),
          catchError(err => of(AdminBranchesActions.failure({ error: this.readError(err) }))),
        );
      }),
    ),
  );

  // ── User Detail (keyed dedup with inFlight Set) ──────────────────

  loadUserDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUserDetailActions.load),
      withLatestFrom(this.store.select(selectAdminUserDetailMap)),
      switchMap(([{ userId, forceReload }, detailMap]) => {
        const slice = detailMap[userId];
        if (!forceReload && slice && isCacheValid(slice.cache)) return EMPTY;
        if (this._inFlight.has(userId)) return EMPTY;
        this._inFlight.add(userId);
        return this.api.getUser(userId).pipe(
          map(data => AdminUserDetailActions.success({ userId, data })),
          catchError(err => of(AdminUserDetailActions.failure({ userId, error: this.readError(err) }))),
          finalize(() => this._inFlight.delete(userId)),
        );
      }),
    ),
  );
}
